import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, twx } from '@ctw/shared/utils/tailwind';
import {
  faThumbsDown as faThumbsDownRegular,
  faThumbsUp as faThumbsUpRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';

export interface ThumbsUpOrDownProps {
  onChange: (value: ThumbStatus) => void;
  className?: ClassName;
  initialValue?: ThumbStatus;
}

export type ThumbStatus = boolean | undefined;

export const FeedbackThumbs = ({ onChange, className, initialValue }: ThumbsUpOrDownProps) => {
  const telemetry = useTelemetry();
  const [thumbStatus, setThumbStatus] = useState<ThumbStatus>(initialValue);

  const handleThumbsUp = useCallback(() => {
    telemetry.trackInteraction('feedback_thumbs_up');

    setThumbStatus(true);
    onChange(true);
  }, [onChange, telemetry.trackInteraction]);

  const handleThumbsDown = useCallback(() => {
    telemetry.trackInteraction('feedback_thumbs_down');

    setThumbStatus(false);
    onChange(false);
  }, [onChange, telemetry.trackInteraction]);

  useEffect(() => {
    // Update value if initial value changes
    setThumbStatus((previousThumbStatus) =>
      previousThumbStatus !== initialValue ? initialValue : previousThumbStatus,
    );
  }, [initialValue]);

  return (
    <div className={twx('flex gap-[10px]', className)}>
      <button
        className={twx(
          'flex aspect-square h-8 w-8 items-center justify-center rounded-full transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          {
            'bg-primary-main': thumbStatus === true,
            'border border-border-main bg-white hover:bg-background-hover': thumbStatus !== true,
          },
        )}
        onClick={handleThumbsUp}
        type="button"
        aria-label="Thumbs up"
      >
        <FontAwesomeIcon
          icon={thumbStatus === true ? faThumbsUpSolid : faThumbsUpRegular}
          className={twx('h-4 w-4 text-content-icon', { 'text-white': thumbStatus === true })}
        />
      </button>
      <button
        className={twx(
          'flex aspect-square h-8 w-8 items-center justify-center rounded-full transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          {
            'bg-primary-main': thumbStatus === false,
            'border border-border-main bg-white hover:bg-background-hover': thumbStatus !== false,
          },
        )}
        type="button"
        aria-label="Thumbs down"
        onClick={handleThumbsDown}
      >
        <FontAwesomeIcon
          icon={thumbStatus === false ? faThumbsDownSolid : faThumbsDownRegular}
          className={twx('h-4 w-4 text-content-icon', { 'text-white': thumbStatus === false })}
        />
      </button>
    </div>
  );
};
